import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useLookup } from '../contexts/LookupContext';
import { Printer, Tag, EnvelopeSimple, FileText, Check, Spinner } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

export default function PrintMergePage() {
  const { activeLookup, clearActiveLookup } = useLookup();
  const [tab, setTab] = useState('labels'); // labels | envelopes | merge
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [templates, setTemplates] = useState([]);
  const [formats, setFormats] = useState({ labels: [], envelopes: [] });
  const [selected, setSelected] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [labelFormat, setLabelFormat] = useState('avery_5160');
  const [envFormat, setEnvFormat] = useState('no10');
  const [returnAddress, setReturnAddress] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [emailStatus, setEmailStatus] = useState({ configured: false, sender: '', gmail_connected: false, gmail_email: '' });
  const [sendProvider, setSendProvider] = useState('resend'); // resend | gmail
  const [sendResult, setSendResult] = useState(null);
  const [stopRequested, setStopRequested] = useState(false);
  const jobPollRef = useRef(null);

  useEffect(() => () => clearInterval(jobPollRef.current), []);

  useEffect(() => {
    (async () => {
      try {
        const [g, t, f, e, tg] = await Promise.all([
          api.get('/groups'),
          api.get('/templates'),
          api.get('/print/formats'),
          api.get('/email/status'),
          api.get('/tags'),
        ]);
        setGroups(g.data || []);
        setTemplates(t.data || []);
        setFormats(f.data || { labels: [], envelopes: [] });
        setEmailStatus(e.data || { configured: false });
        setTags(tg.data || []);
        // Default to Gmail if it's connected — better deliverability
        if (e.data?.gmail_connected) setSendProvider('gmail');
      } catch (err) { console.error(err); }
    })();
  }, []);

  // Contact selector honors the active cross-page lookup (ACT! behavior:
  // "mail-merge just my current lookup"). Looked-up contacts arrive
  // PRE-SELECTED so a merge is one click away. A tag filter, when picked,
  // takes precedence and pre-selects everyone carrying that tag.
  useEffect(() => {
    (async () => {
      try {
        let list = [];
        if (tagFilter) {
          const { data } = await api.get('/contacts', { params: { tag: tagFilter, limit: 1000 } });
          list = data.contacts || [];
          setContacts(list);
          setSelected(list.map(c => c.contact_id));
          return;
        }
        if (activeLookup?.query) {
          const { data } = await api.post('/contacts/lookup?page=1&limit=1000', activeLookup.query);
          list = data.contacts || [];
        } else if (activeLookup?.ids?.length) {
          const { data } = await api.get('/contacts', { params: { ids: activeLookup.ids.join(','), limit: 1000 } });
          list = data.contacts || [];
        } else {
          const { data } = await api.get('/contacts', { params: { limit: 200 } });
          list = data.contacts || [];
        }
        setContacts(list);
        setSelected(activeLookup ? list.map(c => c.contact_id) : []);
      } catch (err) { console.error(err); }
    })();
  }, [activeLookup, tagFilter]);

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.first_name || '').toLowerCase().includes(s)
      || (c.last_name || '').toLowerCase().includes(s)
      || (c.company || '').toLowerCase().includes(s);
  });

  const toggle = (id) => {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };
  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map(c => c.contact_id));
  };

  const openPrintHtml = (html) => {
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const runPrint = async (endpoint, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, body);
      openPrintHtml(data.print_html);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const handleLabels = () => {
    if (!selected.length && !groupId) return alert('Select contacts or a group');
    runPrint('/print/labels', { contact_ids: selected, group_id: groupId || null, format: labelFormat });
  };
  const handleEnvelopes = () => {
    if (!selected.length && !groupId) return alert('Select contacts or a group');
    runPrint('/print/envelopes', {
      contact_ids: selected, group_id: groupId || null,
      format: envFormat, return_address: returnAddress,
    });
  };
  const handleMerge = () => {
    if (!templateId) return alert('Pick a template');
    if (!selected.length && !groupId) return alert('Select contacts or a group');
    runPrint(`/templates/${templateId}/merge-bulk`, { contact_ids: selected, group_id: groupId || null });
  };

  const pollJob = (jobId) => {
    clearInterval(jobPollRef.current);
    jobPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/email/bulk-jobs/${jobId}`);
        setSendResult(data);
        if (data.status === 'done' || data.status === 'error' || data.status === 'cancelled') {
          clearInterval(jobPollRef.current);
          setBusy(false);
        }
      } catch {
        clearInterval(jobPollRef.current);
        setBusy(false);
      }
    }, 2000);
  };

  const stopSending = async () => {
    if (!sendResult?.job_id || stopRequested) return;
    setStopRequested(true);
    try {
      await api.post(`/email/bulk-jobs/${sendResult.job_id}/cancel`);
    } catch { /* the poll will reflect the final state either way */ }
  };

  const handleSendEmails = async () => {
    if (!templateId) return alert('Pick a template');
    if (!selected.length && !groupId) return alert('Select contacts or a group');
    const usingGmail = sendProvider === 'gmail';
    if (usingGmail && !emailStatus.gmail_connected) {
      return alert('Gmail is not connected. Go to Settings → Connect Gmail first.');
    }
    if (!usingGmail && !emailStatus.configured) {
      if (!window.confirm('Resend email is not configured yet. The send will fail. Continue anyway to see the error?')) return;
    }
    setBusy(true); setSendResult(null); setStopRequested(false);
    try {
      const { data } = await api.post('/email/send-bulk', {
        template_id: templateId,
        contact_ids: selected,
        group_id: groupId || null,
        provider: sendProvider,
      });
      setSendResult(data); // {job_id, status: "queued", total, ...}
      pollJob(data.job_id);
    } catch (err) {
      setSendResult({ error: err.response?.data?.detail || 'Failed' });
      setBusy(false);
    }
  };

  const jobRunning = sendResult && !sendResult.error && (sendResult.status === 'queued' || sendResult.status === 'running');
  const jobDone = sendResult && !sendResult.error && sendResult.status === 'done';
  const jobCancelled = sendResult && sendResult.status === 'cancelled';
  const jobErrored = sendResult && sendResult.status === 'error';
  const progressDone = (sendResult?.sent_count || 0) + (sendResult?.failed_count || 0);
  const progressPct = sendResult?.total ? Math.round((progressDone / sendResult.total) * 100) : 0;

  const TAB = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 text-[12px] font-medium border-b-2 transition-colors ${tab === id ? 'border-[#F39C12] text-[#0B5394] bg-white' : 'border-transparent text-[#666] hover:text-[#0B5394]'}`}
      data-testid={`tab-${id}`}
    >
      <Icon size={14} weight="bold" />{label}
    </button>
  );

  return (
    <div className="flex flex-col h-full bg-[#E8E8E8]" data-testid="print-merge-page">
      {/* ACT!-style orange header */}
      <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] shrink-0 flex items-center">
        <h1 className="text-[14px] font-bold text-white flex items-center gap-2"><Printer size={16} weight="bold" />Print & Mail Merge</h1>
        <div className="flex-1" />
        <WatchHowLink slug="labels-envelopes" label="Watch: Labels & Envelopes" light />
        <WatchHowLink slug="mail-merge" label="Watch: Mail Merge" light className="ml-4" />
      </div>

      <div className="flex border-b border-[#999] bg-[#E0E0E0] shrink-0">
        <TAB id="labels" icon={Tag} label="Labels" />
        <TAB id="envelopes" icon={EnvelopeSimple} label="Envelopes" />
        <TAB id="merge" icon={FileText} label="Mail Merge" />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: contacts selector */}
        <div className="w-[380px] border-r border-[#999] bg-white flex flex-col">
          {activeLookup && !tagFilter && (
            <div className="px-2 py-2 bg-[#FFF8E7] border-b border-[#F39C12]/60" data-testid="pm-lookup-banner">
              <div className="text-[11px] text-[#7A4F00]">
                <strong>Lookup:</strong> {activeLookup.label}
                {typeof activeLookup.count === 'number' && <> — {activeLookup.count} contact{activeLookup.count === 1 ? '' : 's'}</>} (pre-selected)
              </div>
              <button
                onClick={clearActiveLookup}
                className="text-[11px] font-bold text-[#0B5394] hover:text-[#002FA7] underline underline-offset-2"
                data-testid="pm-lookup-clear-btn"
              >
                Show all contacts
              </button>
            </div>
          )}
          <div className="p-2 border-b border-[#CCC] space-y-1.5">
            <select
              value={tagFilter}
              onChange={e => setTagFilter(e.target.value)}
              className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
              data-testid="pm-tag-filter"
            >
              <option value="">Filter by tag — all contacts</option>
              {tags.map(t => (
                <option key={t.tag} value={t.tag}>{`${t.tag} (${t.count})`}</option>
              ))}
            </select>
            {tagFilter && (
              <div className="text-[11px] text-[#7A4F00] bg-[#FFF8E7] border border-[#F39C12]/60 px-2 py-1" data-testid="pm-tag-filter-banner">
                <strong>Tag:</strong> {tagFilter} — {contacts.length} contact{contacts.length === 1 ? '' : 's'} (pre-selected)
                <button
                  onClick={() => setTagFilter('')}
                  className="ml-2 font-bold text-[#0B5394] hover:text-[#002FA7] underline underline-offset-2"
                  data-testid="pm-tag-filter-clear"
                >
                  Clear
                </button>
              </div>
            )}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search contacts..."
              className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]"
              data-testid="pm-contact-search"
            />
          </div>
          <div className="p-2 border-b border-[#CCC] text-[11px] flex items-center justify-between bg-[#F5F5F5]">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={filtered.length > 0 && selected.length === filtered.length} onChange={toggleAll} data-testid="pm-select-all" />
              Select all ({filtered.length})
            </label>
            <span className="text-[#666]">{selected.length} selected</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map(c => (
              <label key={c.contact_id} className="flex items-center gap-2 px-2 py-1 text-[12px] hover:bg-[#F0F4FB] cursor-pointer border-b border-[#EEE]" data-testid={`pm-contact-${c.contact_id}`}>
                <input type="checkbox" checked={selected.includes(c.contact_id)} onChange={() => toggle(c.contact_id)} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.first_name} {c.last_name}</div>
                  <div className="text-[10px] text-[#666] truncate">{c.company}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Right: options */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="pm-options-panel">
          <div className="max-w-xl bg-white border border-[#CCC] p-5 shadow-sm">
            {/* Group selector shared */}
            <div className="mb-4">
              <label className="text-[11px] font-semibold text-[#333] block mb-1">Or pick a group (combined with selection)</label>
              <select value={groupId} onChange={e => setGroupId(e.target.value)} className="w-full h-[30px] px-2 text-[12px] border border-[#B0B0B0]" data-testid="pm-group">
                <option value="">— None —</option>
                {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
              </select>
              <p className="text-[10px] text-[#888] mt-1">
                Tip: to target a <strong>tag</strong> instead, use the &quot;Filter by tag&quot; dropdown above the contact list — it pre-selects everyone with that tag.
              </p>
            </div>

            {tab === 'labels' && (
              <div>
                <label className="text-[11px] font-semibold text-[#333] block mb-1">Label format</label>
                <select value={labelFormat} onChange={e => setLabelFormat(e.target.value)} className="w-full h-[30px] px-2 text-[12px] border border-[#B0B0B0] mb-4" data-testid="pm-label-format">
                  {formats.labels.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <button onClick={handleLabels} disabled={busy} className="px-4 py-2 bg-[#0B5394] text-white text-[12px] font-bold hover:bg-[#073763] disabled:opacity-50 flex items-center gap-2" data-testid="pm-print-labels">
                  <Printer size={14} weight="bold" />{busy ? 'Generating...' : 'Generate Labels'}
                </button>
              </div>
            )}

            {tab === 'envelopes' && (
              <div>
                <label className="text-[11px] font-semibold text-[#333] block mb-1">Envelope size</label>
                <select value={envFormat} onChange={e => setEnvFormat(e.target.value)} className="w-full h-[30px] px-2 text-[12px] border border-[#B0B0B0] mb-4" data-testid="pm-env-format">
                  {formats.envelopes.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <label className="text-[11px] font-semibold text-[#333] block mb-1">Return address (optional)</label>
                <textarea value={returnAddress} onChange={e => setReturnAddress(e.target.value)} rows={4} placeholder={"Your Name\nCompany\nStreet\nCity, State ZIP"} className="w-full px-2 py-1 text-[12px] border border-[#B0B0B0] mb-4 font-mono" data-testid="pm-return-address" />
                <button onClick={handleEnvelopes} disabled={busy} className="px-4 py-2 bg-[#0B5394] text-white text-[12px] font-bold hover:bg-[#073763] disabled:opacity-50 flex items-center gap-2" data-testid="pm-print-envelopes">
                  <Printer size={14} weight="bold" />{busy ? 'Generating...' : 'Generate Envelopes'}
                </button>
              </div>
            )}

            {tab === 'merge' && (
              <div>
                <label className="text-[11px] font-semibold text-[#333] block mb-1">Template</label>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)} className="w-full h-[30px] px-2 text-[12px] border border-[#B0B0B0] mb-4" data-testid="pm-template">
                  <option value="">— Choose template —</option>
                  {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name} ({t.template_type})</option>)}
                </select>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <button onClick={handleMerge} disabled={busy} className="px-4 py-2 bg-[#0B5394] text-white text-[12px] font-bold hover:bg-[#073763] disabled:opacity-50 flex items-center gap-2" data-testid="pm-run-merge">
                    <Check size={14} weight="bold" />{busy ? 'Working...' : 'Preview / Print'}
                  </button>

                  {/* Send from picker — always visible so users know both pathways exist */}
                  <div className="flex items-center gap-1 text-[11px] text-[#666]" data-testid="pm-send-from-group">
                    <label htmlFor="pm-send-from" className="font-semibold uppercase tracking-wide">Send via:</label>
                    <select
                      id="pm-send-from"
                      value={sendProvider}
                      onChange={(e) => setSendProvider(e.target.value)}
                      disabled={busy}
                      className="h-[28px] border border-[#B0B0B0] px-2 text-[11px] bg-white"
                      data-testid="pm-send-from-select"
                    >
                      <option value="gmail" disabled={!emailStatus.gmail_connected}>
                        {emailStatus.gmail_connected
                          ? `Gmail (${emailStatus.gmail_email || 'your Gmail'})`
                          : 'Gmail — not connected (Settings → Email)'}
                      </option>
                      <option value="resend" disabled={!emailStatus.configured}>
                        {`SalesCrunch system (${emailStatus.sender})${!emailStatus.configured ? ' — not configured' : ''}`}
                      </option>
                    </select>
                  </div>

                  <button
                    onClick={handleSendEmails}
                    disabled={busy}
                    className={`px-4 py-2 text-white text-[12px] font-bold flex items-center gap-2 disabled:opacity-50 ${(sendProvider === 'gmail' ? emailStatus.gmail_connected : emailStatus.configured) ? 'bg-[#F39C12] hover:bg-[#D68910]' : 'bg-[#999] hover:bg-[#777]'}`}
                    data-testid="pm-send-emails"
                    title={
                      sendProvider === 'gmail'
                        ? (emailStatus.gmail_connected ? `Sends via ${emailStatus.gmail_email}` : 'Connect Gmail in Settings first')
                        : (emailStatus.configured ? `Sends via ${emailStatus.sender}` : 'Resend not configured')
                    }
                  >
                    <EnvelopeSimple size={14} weight="bold" />{busy ? 'Sending...' : 'Send Emails'}
                  </button>
                </div>
                <p className="text-[11px] text-[#666] mb-3" data-testid="pm-send-info">
                  {sendProvider === 'gmail' ? (
                    emailStatus.gmail_connected ? (
                      <>Sending from <strong>{emailStatus.gmail_email}</strong> (your Gmail). Best deliverability for personal outreach. Subject to Gmail&apos;s ~500/day limit.</>
                    ) : (
                      <>Gmail is <strong className="text-[#C0392B]">not connected</strong>. Go to Settings → Email — Connect Gmail.</>
                    )
                  ) : (
                    emailStatus.configured ? (
                      <>Sending from <strong>{emailStatus.sender}</strong> (system). Fast for bulk sends.</>
                    ) : (
                      <>Email sending is <strong className="text-[#C0392B]">not configured</strong>.</>
                    )
                  )}
                </p>

                {jobRunning && (
                  <div className="bg-blue-50 border border-blue-300 text-[12px] p-3 mt-3" data-testid="send-progress">
                    <div className="flex items-center gap-2 font-bold text-blue-900 mb-1.5">
                      <Spinner size={14} className="animate-spin" weight="bold" />
                      Sending… {progressDone.toLocaleString()} of {(sendResult.total || 0).toLocaleString()}
                    </div>
                    <div className="h-[8px] bg-blue-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#0B5394] transition-all duration-500" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <p className="text-[10px] text-blue-800">
                        Each send is logged to the contact&apos;s History.
                      </p>
                      <button
                        onClick={stopSending}
                        disabled={stopRequested}
                        data-testid="pm-stop-send-btn"
                        className="px-3 py-1 text-[11px] font-bold bg-[#C0392B] text-white hover:bg-[#96281B] disabled:opacity-60 rounded-sm shrink-0"
                      >
                        {stopRequested ? 'Stopping…' : '■ Stop sending'}
                      </button>
                    </div>
                  </div>
                )}
                {jobCancelled && (
                  <div className="bg-amber-50 border border-amber-400 text-[12px] p-3 mt-3" data-testid="send-cancelled">
                    <div className="font-bold text-amber-900">
                      Stopped early — sent {sendResult.sent_count} of {(sendResult.total || 0).toLocaleString()} before cancelling. Each sent email is logged in the contact&apos;s History.
                    </div>
                  </div>
                )}
                {jobDone && (
                  <div className="bg-[#F0FDF4] border border-green-400 text-[12px] p-3 mt-3" data-testid="send-result">
                    <div className="font-bold text-green-800">Sent {sendResult.sent_count} · Failed {sendResult.failed_count}</div>
                    {sendResult.failed_count > 0 && (
                      <ul className="mt-1 text-[11px] text-[#C0392B] list-disc pl-5">
                        {(sendResult.failed || []).slice(0, 5).map((f, i) => <li key={i}>{f.email || f.contact_id}: {f.reason}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                {(sendResult?.error || jobErrored) && (
                  <div className="bg-red-50 border border-red-400 text-[12px] p-3 mt-3 text-[#C0392B]" data-testid="send-error">{sendResult.error}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
